import React, { useState } from 'react';
import ItemCategoryFilter from '@/components/filters/ItemCategoryFilter';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { DeadlineFilterData, LinkingType, OptionGroupLinkStatus } from '@/types';
import { DateDisplay } from '@/components/localization';

// import { UnitFilter } from '../../UnitDropdown/UnitDropdown';
import { CompanyFilter } from './CompanyFilter';
import { TypeFilter } from './TypeFilter';
import { RoomFilter } from './RoomFilter';
import { TagFilter } from './TagFilter';
import { OptionGroupFilterData } from '@/services/optionGroups';
import {
    FilterButtons,
    FilterDropDown,
    FilterPopOut,
    useIsFilterModified,
} from '@ygtechnologies/bygr-frontend-component-library';
import { CatalogFilterValue } from '../../catalog/CatalogFilter';
import { UnitFilter } from '../../UnitDropdown/UnitDropdown';
import { useGetProjectId } from '@/utils/hooks';

export type ProjectOptionFilter = CatalogFilterValue & {
    tagIds: number[];
    unitIds: number[];
    deadlines: number[];
    providedByProfileIds?: number[];
    linkType?: LinkingType;
    roomIds?: number[];
    publishedStatus?: OptionGroupLinkStatus[];
};
export function getEmptyProjectOptionFilter(): ProjectOptionFilter {
    return {
        ...CatalogFilterValue.empty(),
        tagIds: [],
        unitIds: [],
        deadlines: [],
        providedByProfileIds: [],
        linkType: undefined,
        roomIds: [],
        publishedStatus: [],
    };
}
import { PublishedStatusFilter } from './PublishedStatusFilter';

interface OptionsFilterProps {
    filter: ProjectOptionFilter;
    onChange: (a: ProjectOptionFilter) => void;
    isLoading: boolean;
    filterData?: OptionGroupFilterData | null;
    isFilterOpen?: boolean;
    onFilterOpenChange?: (open: boolean) => void;
    getEmptyFilter?: () => ProjectOptionFilter;
}

export const OptionsFilter: React.FC<OptionsFilterProps> = ({
    filter,
    onChange,
    isLoading,
    filterData,
    isFilterOpen: controlledIsFilterOpen,
    onFilterOpenChange,
    getEmptyFilter,
}) => {
    const [internalIsFilterOpen, setInternalIsFilterOpen] = useState(false);
    const projectId = useGetProjectId();

    const emptyFilter = getEmptyFilter ?? getEmptyProjectOptionFilter;
    const filterModified = useIsFilterModified(filter, emptyFilter);

    const isFilterOpen = controlledIsFilterOpen !== undefined ? controlledIsFilterOpen : internalIsFilterOpen;
    const setIsFilterOpen = (value: boolean | ((prev: boolean) => boolean)) => {
        if (!onFilterOpenChange) return setInternalIsFilterOpen(value);

        const newValue = typeof value === 'function' ? value(isFilterOpen) : value;
        onFilterOpenChange(newValue);
    };

    return (
        <>
            <FilterButtons
                searchText={filter.searchText}
                onSearchTextChange={(v) => onChange({ ...filter, searchText: v })}
                isLoading={isLoading}
                onButtonClick={() => setIsFilterOpen((v) => !v)}
                isActive={filterModified}
            />

            <FilterPopOut
                onClear={() => onChange(emptyFilter())}
                onClose={() => setIsFilterOpen(false)}
                isVisible={isFilterOpen}>
                <label className="filter-label">
                    <Trans>Categories</Trans>
                </label>
                <ItemCategoryFilter
                    value={filter.itemCategoryIds}
                    onChange={(v) => onChange({ ...filter, itemCategoryIds: v })}
                />
                <TagFilter
                    projectId={projectId}
                    value={filter.tagIds}
                    onChange={(v) => onChange({ ...filter, tagIds: v })}
                />

                <TypeFilter value={filter.linkType} onChange={(v) => onChange({ ...filter, linkType: v })} />

                <PublishedStatusFilter
                    value={filter.publishedStatus || []}
                    onChange={(v) => onChange({ ...filter, publishedStatus: v })}
                />

                {filterData && (
                    <>
                        <CompanyFilter
                            value={filter.providedByProfileIds}
                            onChange={(v) => onChange({ ...filter, providedByProfileIds: v })}
                            companies={filterData.companies}
                        />

                        <RoomFilter
                            value={filter.roomIds}
                            onChange={(v) => onChange({ ...filter, roomIds: v })}
                            rooms={filterData.rooms}
                        />

                        <UnitFilter
                            value={filter.unitIds}
                            onChange={(v) => onChange({ ...filter, unitIds: v })}
                            units={filterData.units}
                        />

                        <FilterDropDown
                            placeholder={t`Any deadline`}
                            label={<Trans>Deadlines</Trans>}
                            data={filterData.deadlines.map((d: DeadlineFilterData) => ({
                                id: d.id,
                                name: (
                                    <>
                                        {d.name} - <DateDisplay value={d.deadline} />
                                    </>
                                ),
                            }))}
                            value={filter.deadlines}
                            onChange={(v) => onChange({ ...filter, deadlines: v.map((x) => x.id) })}
                        />
                    </>
                )}
            </FilterPopOut>
        </>
    );
};
